import { Box, IconButton, IconCloseX16, IconInformation16, IconSuccess16, Toast, ToastProps } from '@stitch-fix/mode-react';
export interface ToastInfo {
  /**
   * The toast message.
   */
  message: string;
  /**
   * Sets the toast start content icon:
   * - `success`: displays a success icon.
   * - `error`: displays an info icon.
   */
  status?: 'success' | 'error';
  /**
   * Sets the toast's endContent.
   */
  endContent?: ToastProps['endContent'];
  /**
   * Determines if there is a manual close
   */
  manualClose?: boolean;
  /**
   * Toast duration in milliseconds.
   * Defaults to 4000 if not provided (internally in Mode's Toast)
   */
  delay?: number;
}
interface PageToastProps extends ToastInfo {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Function that builds a manual close button for toast
 */
const manualCloseButton = (onClose: () => void) => {
  return <Box my={-0.25} mx={-0.5}>
      <IconButton aria-label="close" variant="inherited" onClick={onClose}>
        <IconCloseX16 />
      </IconButton>
    </Box>;
};

/**
 * Determines the startContent for toast based on received status
 */
const startContentByStatus = (status: ToastInfo['status']) => {
  switch (status) {
    case 'error':
      return <IconInformation16 title={status} />;
    case 'success':
      return <IconSuccess16 title={status} />;
    default:
      return false;
  }
};

/**
 * `PageToast` component is used in `ToastProvider` to show toasts on a page.
 *
 * Avoid using this component directly; use the `useToast` hook to display toasts when needed.
 *
 * Call `addToast({ message: 'hello' })` to show PageToast with the provided message.
 * Optionally, you can pass `status` and `endContent`.
 */
const PageToast = ({
  isOpen,
  message,
  status,
  endContent,
  onClose,
  manualClose,
  delay = undefined
}: PageToastProps) => {
  return <Toast isOpen={isOpen} delay={delay} mb={{
    sm: 4,
    md: 0
  }} onClose={manualClose ? undefined : onClose} startContent={startContentByStatus(status)} endContent={manualClose ? manualCloseButton(onClose) : endContent}>
      {message}
    </Toast>;
};
export default PageToast;